import Vue from "vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

/** What the Exit Auto Mode button has always said, and what the guided tour says too. */
export const AUTO_MODE_EXIT_MESSAGE = "Auto Mode has been exited";

export interface ExitAutoModeOptions {
    /** Replaces the default message, e.g. the API's "All items completed!" text. */
    message?: string;
    type?: NotificationOptions.NotificationTypes;
    /** Milliseconds; defaults to the notification preset's own timeout. */
    timeout?: number;
}

/**
 * Turns Auto Mode off and tells the user.
 *
 * Auto Mode's state lives in five separate properties on the observable root store, and forgetting
 * one leaves the next session skipping items it never showed. Two places used to clear it by hand --
 * the Exit Auto Mode button and the hotkey mixin's "no items left" path -- and they disagreed: the
 * button left `autoModeRoutedItemIds` and `autoModeRoutedOnlineOrderIds` behind. Both entry points
 * re-seed those two arrays, so clearing them here is not a behaviour change, only a consistent one.
 *
 * Returns false and does nothing at all when Auto Mode is already off, so a caller that just wants
 * to be sure it is off -- the guided tour, before its first step -- does not flash a notification
 * at a user who was never in Auto Mode. Nothing here turns Auto Mode back on.
 */
export function exitAutoMode(vm: Vue, options: ExitAutoModeOptions = {}): boolean {
    const store = (vm as any).$store;
    if (!store?.autoModeEnabled) return false;

    store.autoModeEnabled = false;
    store.currentAutoModeQueue = null;
    store.lastAutoModeResponse = null;
    store.autoModeRoutedItemIds = [];
    store.autoModeRoutedOnlineOrderIds = [];

    const notification = NotificationOptions.notificationOptionsPreset(
        options.message ?? AUTO_MODE_EXIT_MESSAGE,
        options.type ?? NotificationOptions.NotificationTypes.info
    );
    if (options.timeout !== undefined) notification.timeout = options.timeout;

    (vm as any).$notification(notification);
    return true;
}
